import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.#setupInstantSwatchSwitching(signal);
  }

  #controller = new AbortController();
  #lastSelectedMediaId = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #setupInstantSwatchSwitching(signal) {
    const section = this.closest('.shopify-section') || document;
    
    // When any variant radio/swatch is clicked or changed
    section.addEventListener('change', (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      if (input.closest('product-card')) return;

      this.#switchImageForInput(input);
    }, { signal });

    // Also on click for immediate response before change event
    section.addEventListener('click', (e) => {
      const label = e.target instanceof HTMLElement ? e.target.closest('.variant-option__button-label, .variant-option label') : null;
      if (!label) return;
      const input = label.querySelector('input[type="radio"]');
      if (input && !input.closest('product-card')) {
        this.#switchImageForInput(input);
      }
    }, { signal });
  }

  #switchImageForInput(input, force = true) {
    const mediaId = input.dataset.mediaId;
    const mediaUrl = input.dataset.mediaUrl;
    const optVal = (input.value || '').toLowerCase().trim();

    if (!force && mediaId && this.#lastSelectedMediaId === mediaId) {
      return;
    }

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const slides = slideshow.slides?.length ? slideshow.slides : Array.from(slideshow.querySelectorAll('slideshow-slide, .product-media-container'));
    if (!slides.length) return;

    // 1. Try finding slide by data-media-id
    if (mediaId) {
      const targetIndex = slides.findIndex((slide) => {
        const sMediaId = slide.dataset.mediaId || slide.getAttribute('slide-id');
        return (
          sMediaId === mediaId ||
          slide.querySelector(`[data-media-id="${mediaId}"]`) !== null
        );
      });
      if (targetIndex !== -1) {
        this.#lastSelectedMediaId = mediaId;
        slideshow.select(targetIndex, undefined, { animate: false });
        this.#updateThumbnails(targetIndex);
        return;
      }
    }

    // 2. Try finding slide by matching image alt or filename with option value (e.g. "White", "Black")
    if (optVal) {
      const targetIndex = slides.findIndex((slide) => {
        const img = slide.querySelector('img');
        if (!img) return false;
        const alt = (img.getAttribute('alt') || '').toLowerCase();
        const src = (img.getAttribute('src') || '').toLowerCase();
        return alt.includes(optVal) || src.includes(optVal);
      });
      if (targetIndex !== -1) {
        this.#lastSelectedMediaId = mediaId || optVal;
        slideshow.select(targetIndex, undefined, { animate: false });
        this.#updateThumbnails(targetIndex);
        return;
      }
    }

    // 3. If there is a direct media URL (e.g. combined listing product), update the active slide image immediately
    if (mediaUrl) {
      const currentSlide = slides[slideshow.current || 0] || slides[0];
      const img = currentSlide?.querySelector('img.product-media__image, img');
      if (img) {
        img.src = mediaUrl;
        img.srcset = mediaUrl;
      }
    }
  }

  #updateThumbnails(index) {
    const thumbnails = this.querySelectorAll('.slideshow-controls__thumbnail, .product-media-gallery__thumbnails .thumbnail-item, .thumbnail-item');
    thumbnails.forEach((t, i) => {
      if (i === index) {
        t.setAttribute('aria-selected', 'true');
        t.setAttribute('aria-current', 'true');
        t.classList.add('is-active');
        try {
          t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        } catch (_) {}
      } else {
        t.removeAttribute('aria-selected');
        t.removeAttribute('aria-current');
        t.classList.remove('is-active');
      }
    });
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { variantId, optionValueId } = event.detail || {};
    if (variantId || optionValueId) {
      const input = document.querySelector(`input[data-variant-id="${variantId}"], input[data-option-value-id="${optionValueId}"]`);
      if (input) {
        this.#switchImageForInput(input, false);
      }
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const currentMediaIds = Array.from(this.querySelectorAll('slideshow-slide[data-media-id], [ref="media[]"][data-media-id]'))
          .map(el => el.dataset.mediaId)
          .filter(Boolean);
        const newMediaIds = Array.from(newMediaGallery.querySelectorAll('slideshow-slide[data-media-id], [ref="media[]"][data-media-id]'))
          .map(el => el.dataset.mediaId)
          .filter(Boolean);

        const currentSet = new Set(currentMediaIds);
        const newSet = new Set(newMediaIds);
        const hasDifferentMedia = currentMediaIds.length !== newMediaIds.length ||
          newMediaIds.some(id => !currentSet.has(id)) ||
          currentMediaIds.some(id => !newSet.has(id));

        if (hasDifferentMedia && newMediaIds.length > 0) {
          this.replaceWith(newMediaGallery);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}